import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Loader2, ChevronLeft, ChevronRight, CheckCircle2, FileUp, PenLine, Plus, Trash2, Clock } from "lucide-react";
import { PortalLayout } from "../../components/PortalLayout";
import { UploadTile } from "../../components/UploadTile";
import { ResumeReviewCard } from "../../components/ResumeReviewCard";
import { api, formatApiError } from "../../lib/api";

const STEPS = ["Your CV", "Your details", "Work rights & visa", "Uploads", "Consent & submit"];
const inputCls = "mt-1.5 w-full rounded-lg border border-[#e3e0d3] bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#94a01a]";
const labelCls = "text-xs uppercase tracking-[0.15em] font-medium text-[#51637a]";
const yesNo = (v) => (v === true ? "Yes" : v === false ? "No" : "");

export default function Onboarding() {
  const [params] = useSearchParams();
  const [step, setStep] = useState(() => Math.min(4, Math.max(0, parseInt(params.get("step"), 10) || 0)));
  const [profile, setProfile] = useState(null);
  const [docs, setDocs] = useState([]);
  const [consents, setConsents] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [signature, setSignature] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [builderReq, setBuilderReq] = useState(null);
  const [cvMode, setCvMode] = useState(null);
  const [payBusy, setPayBusy] = useState(false);
  const [builder, setBuilder] = useState({
    roles: [{ title: "", company: "", duration: "", description: "" }],
    skills: "",
    qualifications: "",
    goals: "",
  });
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/candidate/dashboard").then((res) => {
      setProfile(res.data.profile);
      setDocs(res.data.documents);
      setConsents(res.data.consents);
      if (res.data.profile.extracted_cv) setExtracted(res.data.profile.extracted_cv);
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/candidate/cv-builder").then((r) => {
      setBuilderReq(r.data);
      if (r.data && r.data.request_type === "build" && r.data.answers) {
        setBuilder({
          roles: (r.data.answers.roles && r.data.answers.roles.length)
            ? r.data.answers.roles
            : [{ title: "", company: "", duration: "", description: "" }],
          skills: r.data.answers.skills
            ? (Array.isArray(r.data.answers.skills) ? r.data.answers.skills.join(", ") : r.data.answers.skills)
            : "",
          qualifications: r.data.answers.qualifications
            ? (Array.isArray(r.data.answers.qualifications) ? r.data.answers.qualifications.join("\n") : r.data.answers.qualifications)
            : "",
          goals: r.data.answers.goals || "",
        });
        setCvMode("build");
      }
    }).catch(() => {});
  }, []);

  const saveProfile = async (updates, silent) => {
    setSaving(true);
    try {
      const { data } = await api.put("/candidate/profile", updates);
      setProfile(data);
      if (!silent) toast.success("Saved");
      return true;
    } catch (e) {
      toast.error(formatApiError(e));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const parseCV = async () => {
    setParsing(true);
    try {
      const { data } = await api.post("/candidate/cv/parse");
      setExtracted(data.extracted);
      toast.success("CV read — review the details below");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setParsing(false);
    }
  };

  const applyExtracted = async () => {
    const u = {};
    if (extracted.first_name) u.first_name = extracted.first_name;
    if (extracted.last_name) u.last_name = extracted.last_name;
    if (extracted.phone) u.mobile = extracted.phone;
    if (extracted.suburb || extracted.location) u.location = extracted.suburb || extracted.location;
    if (extracted.headline) u.headline = extracted.headline;
    if (extracted.summary) u.summary = extracted.summary;
    if (extracted.languages) u.languages = Array.isArray(extracted.languages) ? extracted.languages.join(", ") : extracted.languages;
    if (typeof extracted.drivers_licence === "boolean") u.drivers_licence = extracted.drivers_licence;
    if (extracted.skills?.length) u.skills = extracted.skills;
    if (extracted.work_history?.length) u.work_history = extracted.work_history;
    const emp = {};
    if (extracted.current_employer) emp.current_employer = extracted.current_employer;
    if (extracted.current_role) emp.current_role = extracted.current_role;
    if (extracted.years_experience) emp.years_experience = String(extracted.years_experience);
    if (extracted.notice_period) emp.notice_period = extracted.notice_period;
    if (Object.keys(emp).length) u.employment = { ...(profile.employment || {}), ...emp };
    const quals = [
      ...(extracted.qualifications || []),
      ...(extracted.certifications || []),
      ...(extracted.education || []).map((ed) => [ed.qualification, ed.institution, ed.year].filter(Boolean).join(" — ")),
    ].filter(Boolean);
    if (quals.length) u.qualifications = quals;
    const qd = {};
    if (extracted.cert_iv_licence) qd.cert_iv_licence = extracted.cert_iv_licence;
    if (quals.length) qd.other_qualifications = quals.join("\n");
    if (Object.keys(qd).length) u.qualifications_details = { ...(profile.qualifications_details || {}), ...qd };
    const swU = {};
    if (extracted.software?.length) swU.other_software = extracted.software.join(", ");
    if (extracted.key_strengths?.length) swU.key_strengths = Array.isArray(extracted.key_strengths) ? extracted.key_strengths.join(", ") : extracted.key_strengths;
    if (Object.keys(swU).length) u.software_skills = { ...(profile.software_skills || {}), ...swU };
    if (await saveProfile(u, true)) {
      toast.success("Profile filled in from your CV — review your details next");
      setStep(1);
    }
  };

  const signConsent = async () => {
    try {
      await api.post("/consent/sign", { document_name: "Candidate Consent & Privacy Agreement", typed_signature: signature, agreed });
      toast.success("Consent signed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const submitProfile = async () => {
    if (await saveProfile({ submitted: true }, true)) {
      toast.success("Profile submitted for recruiter review!");
      navigate("/candidate");
    }
  };

  const startCheckout = async (request_type, answers) => {
    if (payBusy) return;
    setPayBusy(true);
    try {
      await api.post("/candidate/cv-builder", { request_type, answers });
      const { data } = await api.post("/payments/checkout", { lookup_key: "resume_review_builder", origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setPayBusy(false);
    }
  };

  const setRole = (i, k, v) => {
    const roles = [...builder.roles];
    roles[i] = { ...roles[i], [k]: v };
    setBuilder({ ...builder, roles });
  };

  if (!profile)
    return <PortalLayout><div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#0d2f4d]" /></div></PortalLayout>;

  if (profile.careers_access === false)
    return (
      <PortalLayout>
        <div className="max-w-xl py-10" data-testid="onboarding-locked">
          <p className="font-accent text-lg text-[#7C8514]">Careers access locked</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0d2f4d] tracking-tight mt-2">
            Careers access is part of your relocation packages — choose one to get started.
          </h1>
          <p className="text-sm text-[#51637a] mt-4 leading-relaxed">
            Since you're still overseas, your careers profile unlocks with any of our relocation
            package — Landing, Settlement, Full Concierge or Family Move. Head to your dashboard to choose
            a package, or re-check if you've already purchased.
          </p>
          <button onClick={() => navigate("/candidate")} data-testid="onboarding-locked-back"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#0d2f4d] text-white text-sm font-medium px-7 py-3 hover:bg-[#1c4d78] transition-colors">
            Back to my dashboard
          </button>
        </div>
      </PortalLayout>
    );

  const country = profile.country_of_origin;
  const wr = profile.work_rights || {};
  const setWr = (k, v) => setProfile({ ...profile, work_rights: { ...wr, [k]: v } });
  const nr = profile.next_role || {};
  const setNr = (k, v) => setProfile({ ...profile, next_role: { ...nr, [k]: v } });
  const emp = profile.employment || {};
  const setEmp = (k, v) => setProfile({ ...profile, employment: { ...emp, [k]: v } });
  const qd = profile.qualifications_details || {};
  const setQd = (k, v) => setProfile({ ...profile, qualifications_details: { ...qd, [k]: v } });
  const sw = profile.software_skills || {};
  const setSw = (k, v) => setProfile({ ...profile, software_skills: { ...sw, [k]: v } });
  const hasCv = docs.some((d) => d.doc_type === "cv");

  return (
    <PortalLayout>
      <div data-testid="onboarding-page">
        <p className="text-xs uppercase tracking-[0.25em] text-[#7C8514] font-medium">My profile</p>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0d2f4d] tracking-tight mt-2">Profile & onboarding</h1>

        <div className="flex gap-2 mt-8 overflow-x-auto pb-1" data-testid="onboarding-steps">
          {STEPS.map((s, i) => (
            <button key={s} onClick={() => setStep(i)} data-testid={`step-tab-${i}`}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-xs font-medium transition-colors ${
                i === step ? "bg-[#0d2f4d] text-white" : "bg-white border border-[#e3e0d3] text-[#51637a] hover:border-[#0d2f4d]"}`}>
              {i + 1}. {s}
            </button>
          ))}
        </div>

        <div className="ditch-card p-6 sm:p-8 mt-6">
          {step === 1 && (
            <div className="space-y-6 max-w-xl" data-testid="step-personal-details">
              <div className="space-y-4">
                <h2 className="font-heading font-bold text-lg text-[#0d2f4d]">Your details</h2>
                <p className="text-xs text-[#51637a]">If you uploaded a CV, we've pre-filled what we could — review, fix and add anything missing.</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className={labelCls}>First name</label>
                    <input value={profile.first_name || ""} onChange={(e) => setProfile({ ...profile, first_name: e.target.value })} data-testid="profile-firstname" className={inputCls} /></div>
                  <div><label className={labelCls}>Last name</label>
                    <input value={profile.last_name || ""} onChange={(e) => setProfile({ ...profile, last_name: e.target.value })} data-testid="profile-lastname" className={inputCls} /></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className={labelCls}>Mobile number</label>
                    <input value={profile.mobile || ""} onChange={(e) => setProfile({ ...profile, mobile: e.target.value })} data-testid="profile-mobile" className={inputCls} /></div>
                  <div><label className={labelCls}>Suburb</label>
                    <input value={profile.location || ""} placeholder="e.g. Bondi, NSW" onChange={(e) => setProfile({ ...profile, location: e.target.value })} data-testid="profile-location" className={inputCls} /></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className={labelCls}>Driver's licence</label>
                    <select value={yesNo(profile.drivers_licence)} onChange={(e) => setProfile({ ...profile, drivers_licence: e.target.value === "Yes" ? true : e.target.value === "No" ? false : null })} data-testid="profile-drivers-licence" className={inputCls}>
                      <option value="">Select</option><option>Yes</option><option>No</option>
                    </select></div>
                  <div><label className={labelCls}>Own vehicle</label>
                    <select value={yesNo(profile.own_vehicle)} onChange={(e) => setProfile({ ...profile, own_vehicle: e.target.value === "Yes" ? true : e.target.value === "No" ? false : null })} data-testid="profile-own-vehicle" className={inputCls}>
                      <option value="">Select</option><option>Yes</option><option>No</option>
                    </select></div>
                </div>
                <div><label className={labelCls}>Languages spoken</label>
                  <input value={profile.languages || ""} placeholder="e.g. English, Mandarin" onChange={(e) => setProfile({ ...profile, languages: e.target.value })} data-testid="profile-languages" className={inputCls} /></div>
              </div>

              <div className="border-t border-[#e3e0d3] pt-5 space-y-4">
                <h3 className="font-heading font-bold text-[#0d2f4d]">Employment details</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className={labelCls}>Current employer</label>
                    <input value={emp.current_employer || ""} onChange={(e) => setEmp("current_employer", e.target.value)} data-testid="emp-current-employer" className={inputCls} /></div>
                  <div><label className={labelCls}>Current role</label>
                    <input value={emp.current_role || ""} onChange={(e) => setEmp("current_role", e.target.value)} data-testid="emp-current-role" className={inputCls} /></div>
                  <div><label className={labelCls}>Years of experience</label>
                    <input value={emp.years_experience || ""} placeholder="e.g. 5" onChange={(e) => setEmp("years_experience", e.target.value)} data-testid="emp-years-experience" className={inputCls} /></div>
                  <div><label className={labelCls}>Notice period</label>
                    <select value={emp.notice_period || ""} onChange={(e) => setEmp("notice_period", e.target.value)} data-testid="emp-notice-period" className={inputCls}>
                      <option value="">Select</option><option>Available now</option><option>1 week</option><option>2 weeks</option><option>4 weeks</option><option>1-3 months</option><option>3+ months</option>
                    </select></div>
                  <div><label className={labelCls}>Current salary</label>
                    <input value={emp.current_salary || ""} placeholder="e.g. $85k + super" onChange={(e) => setEmp("current_salary", e.target.value)} data-testid="emp-current-salary" className={inputCls} /></div>
                  <div><label className={labelCls}>Salary expectations</label>
                    <input value={emp.salary_expectations || ""} placeholder="e.g. $95k+" onChange={(e) => setEmp("salary_expectations", e.target.value)} data-testid="emp-salary-expectations" className={inputCls} /></div>
                </div>
              </div>

              <div className="border-t border-[#e3e0d3] pt-5 space-y-4">
                <h3 className="font-heading font-bold text-[#0d2f4d]">Qualifications</h3>
                <div><label className={labelCls}>Certificates / Licences</label>
                  <input value={qd.cert_iv_licence || ""} placeholder="e.g. Cert IV, trade licence, RSA, White Card" onChange={(e) => setQd("cert_iv_licence", e.target.value)} data-testid="qual-cert-iv" className={inputCls} /></div>
                <div><label className={labelCls}>Other qualifications</label>
                  <textarea rows={3} value={qd.other_qualifications || ""} placeholder={"One per line"} onChange={(e) => setQd("other_qualifications", e.target.value)} data-testid="qual-other" className={inputCls} /></div>
              </div>

              <div className="border-t border-[#e3e0d3] pt-5 space-y-4">
                <h3 className="font-heading font-bold text-[#0d2f4d]">Job preferences</h3>
                <p className="text-xs text-[#51637a]">This helps our recruiters match you — employers see it when you're shortlisted.</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className={labelCls}>Preferred role</label>
                    <input value={nr.desired_role || ""} placeholder="e.g. Head Chef, Account Manager, Site Supervisor" onChange={(e) => setNr("desired_role", e.target.value)} data-testid="nextrole-desired" className={inputCls} /></div>
                  <div><label className={labelCls}>Preferred suburbs / areas</label>
                    <input value={nr.locations || ""} placeholder="e.g. Eastern Suburbs, CBD" onChange={(e) => setNr("locations", e.target.value)} data-testid="nextrole-locations" className={inputCls} /></div>
                  <div><label className={labelCls}>Full-time or Part-time</label>
                    <select value={nr.work_type || ""} onChange={(e) => setNr("work_type", e.target.value)} data-testid="nextrole-work-type" className={inputCls}>
                      <option value="">Select</option><option>Full-time</option><option>Part-time</option><option>Either</option>
                    </select></div>
                  <div><label className={labelCls}>Hybrid preference</label>
                    <select value={nr.hybrid || ""} onChange={(e) => setNr("hybrid", e.target.value)} data-testid="nextrole-hybrid" className={inputCls}>
                      <option value="">Select</option><option>In office</option><option>Hybrid</option><option>Remote</option><option>Flexible</option>
                    </select></div>
                </div>
              </div>

              <div className="border-t border-[#e3e0d3] pt-5 space-y-4">
                <h3 className="font-heading font-bold text-[#0d2f4d]">Skills &amp; software <span className="font-accent font-medium text-sm text-[#51637a]">(optional)</span></h3>
                <div><label className={labelCls}>Key strengths</label>
                  <textarea rows={2} value={sw.key_strengths || ""} placeholder="e.g. client relationships, negotiation, time management" onChange={(e) => setSw("key_strengths", e.target.value)} data-testid="sw-key-strengths" className={inputCls} /></div>
                <div><label className={labelCls}>Software &amp; tools you've used</label>
                  <input value={sw.other_software || ""} placeholder="e.g. Xero, Salesforce, Excel, MYOB" onChange={(e) => setSw("other_software", e.target.value)} data-testid="sw-other" className={inputCls} /></div>
              </div>

              <button onClick={() => saveProfile({ first_name: profile.first_name, last_name: profile.last_name, mobile: profile.mobile,
                  location: profile.location, languages: profile.languages,
                  ...(profile.drivers_licence !== null && profile.drivers_licence !== undefined ? { drivers_licence: profile.drivers_licence } : {}),
                  ...(profile.own_vehicle !== null && profile.own_vehicle !== undefined ? { own_vehicle: profile.own_vehicle } : {}),
                  employment: emp, qualifications_details: qd, next_role: nr, software_skills: sw })}
                disabled={saving} data-testid="save-personal-details"
                className="rounded-full bg-[#0d2f4d] text-white text-sm font-medium px-6 py-3 hover:bg-[#1c4d78] transition-colors disabled:opacity-60">
                {saving ? "Saving..." : "Save details"}
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5 max-w-xl" data-testid="step-work-rights">
              <h2 className="font-heading font-bold text-lg text-[#0d2f4d]">Work rights & visa — {country}</h2>
              {country === "Australia" && (
                <div><label className={labelCls}>Your work-right status in Australia</label>
                  <select value={wr.status || ""} onChange={(e) => setWr("status", e.target.value)} data-testid="workrights-status" className={inputCls}>
                    <option value="">Select status</option>
                    <option>Australian citizen</option>
                    <option>Permanent resident</option>
                    <option>Working holiday visa</option>
                    <option>Other visa holder</option>
                  </select></div>
              )}
              {country === "New Zealand" && (
                <>
                  <div><label className={labelCls}>Your work-right status</label>
                    <select value={wr.status || ""} onChange={(e) => setWr("status", e.target.value)} data-testid="workrights-status" className={inputCls}>
                      <option value="">Select status</option>
                      <option>NZ citizen (full AU work rights)</option>
                      <option>NZ permanent resident</option>
                      <option>Other visa holder</option>
                    </select></div>
                  <div><label className={labelCls}>Open to relocating to Australia?</label>
                    <select value={wr.relocation || ""} onChange={(e) => setWr("relocation", e.target.value)} data-testid="workrights-relocation" className={inputCls}>
                      <option value="">Select</option>
                      <option>Yes — within 3 months</option>
                      <option>Yes — within 6-12 months</option>
                      <option>Staying in New Zealand</option>
                    </select></div>
                </>
              )}
              {country && country !== "Australia" && country !== "New Zealand" && (
                <>
                  <div><label className={labelCls}>Do you currently hold a visa for Australia or New Zealand?</label>
                    <select value={wr.status || ""} onChange={(e) => setWr("status", e.target.value)} data-testid="workrights-status" className={inputCls}>
                      <option value="">Select</option>
                      <option>Yes — working holiday visa</option>
                      <option>Yes — skilled/work visa</option>
                      <option>No — I need visa sponsorship</option>
                      <option>No — planning to apply myself</option>
                    </select></div>
                  <div><label className={labelCls}>Visa type / details (if any)</label>
                    <input value={wr.visa_type || ""} onChange={(e) => setWr("visa_type", e.target.value)} data-testid="workrights-visatype" className={inputCls} /></div>
                  <div><label className={labelCls}>Do you require sponsorship?</label>
                    <select value={wr.requires_sponsorship || ""} onChange={(e) => setWr("requires_sponsorship", e.target.value)} data-testid="workrights-sponsorship" className={inputCls}>
                      <option value="">Select</option><option>Yes</option><option>No</option><option>Unsure</option>
                    </select></div>
                </>
              )}
              <div><label className={labelCls}>Anything else about your work rights?</label>
                <textarea rows={3} value={wr.notes || ""} onChange={(e) => setWr("notes", e.target.value)} data-testid="workrights-notes" className={inputCls} /></div>
              <button onClick={() => saveProfile({ work_rights: wr })} disabled={saving} data-testid="save-work-rights"
                className="rounded-full bg-[#0d2f4d] text-white text-sm font-medium px-6 py-3 hover:bg-[#1c4d78] transition-colors disabled:opacity-60">
                {saving ? "Saving..." : "Save work rights"}
              </button>
            </div>
          )}

          {step === 0 && (
            <div className="space-y-6" data-testid="step-cv-review">
              <h2 className="font-heading font-bold text-lg text-[#0d2f4d]">Start with your CV</h2>
              <p className="text-sm text-[#51637a] leading-relaxed max-w-2xl -mt-3">
                Got a CV? Upload it and our AI fills out your profile for you — then you just review and tweak.
                No CV? We'll build one with you.
              </p>
              <button type="button" onClick={() => setStep(1)} data-testid="skip-cv-step"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-[#7C8514] hover:gap-2.5 transition-all -mt-2">
                Skip for now — I'll add my CV later <ChevronRight size={15} />
              </button>

              {builderReq && ["in_progress", "awaiting_signoff", "completed"].includes(builderReq.status) && (
                <div className="flex items-start gap-3 bg-[#C1CC24]/10 border border-[#C1CC24] rounded-lg p-5" data-testid="cv-crafting-banner">
                  <Clock size={18} className="text-[#7C8514] mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-[#0d2f4d]">
                      {builderReq.status !== "completed" ? "Your CV is being crafted — ready within 48 hours." : "Your curated CV is ready."}
                    </p>
                    <p className="text-xs text-[#51637a] mt-1">
                      {builderReq.status !== "completed"
                        ? "Our team is working on it. We'll be in touch as soon as it's done — no action needed from you."
                        : "Check your documents — your market-ready CV is waiting there."}
                    </p>
                  </div>
                </div>
              )}

              {(!builderReq || builderReq.status === "draft") && (
              <div className="grid sm:grid-cols-2 gap-4" data-testid="cv-mode-choice">
                <button onClick={() => setCvMode("upload")} data-testid="cv-mode-upload"
                  className={`text-left rounded-lg border p-5 transition-colors ${cvMode === "upload" ? "border-[#0d2f4d] bg-[#0d2f4d]/5" : "border-[#e3e0d3] bg-white hover:border-[#0d2f4d]"}`}>
                  <FileUp size={20} className="text-[#7C8514]" />
                  <p className="font-heading font-bold text-[#0d2f4d] mt-3">I have a CV</p>
                  <p className="text-xs text-[#51637a] mt-1">Upload it and we'll build your profile from it automatically. Free.</p>
                </button>
                <button onClick={() => setCvMode("build")} data-testid="cv-mode-build"
                  className={`text-left rounded-lg border p-5 transition-colors ${cvMode === "build" ? "border-[#0d2f4d] bg-[#0d2f4d]/5" : "border-[#e3e0d3] bg-white hover:border-[#0d2f4d]"}`}>
                  <PenLine size={20} className="text-[#7C8514]" />
                  <p className="font-heading font-bold text-[#0d2f4d] mt-3">I don't have a CV — build it with me</p>
                  <p className="text-xs text-[#51637a] mt-1">Answer a few questions about your roles and our team will curate a professional CV for you. A$207.</p>
                </button>
              </div>
              )}

              {cvMode === "upload" && (!builderReq || builderReq.status === "draft") && (
                <>
                  <UploadTile docType="cv" label="Your CV" hint="PDF, DOCX or TXT" accept=".pdf,.docx,.txt" existingDocs={docs} onChange={load} />
                  <button onClick={parseCV} disabled={!hasCv || parsing} data-testid="parse-cv-button"
                    className="inline-flex items-center gap-2 rounded-full bg-[#C1CC24] text-[#0d2f4d] text-sm font-medium px-6 py-3 hover:bg-[#aeb81f] transition-colors disabled:opacity-50">
                    {parsing ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                    {parsing ? "Reading your CV..." : "Read my CV & fill my profile"}
                  </button>
                  {!hasCv && <p className="text-xs text-[#51637a]">Upload a CV first so we can build your profile from it.</p>}
                  {extracted && (
                    <div className="border border-[#C1CC24] bg-[#C1CC24]/10 rounded-lg p-5" data-testid="extracted-cv-panel">
                      <p className="flex items-center gap-2 text-sm font-medium text-[#0d2f4d]"><Sparkles size={15} className="text-[#7C8514]" /> From your CV — review before applying to your profile</p>
                      <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
                        <p><span className="text-[#51637a]">Name:</span> {extracted.first_name} {extracted.last_name}</p>
                        <p><span className="text-[#51637a]">Email:</span> {extracted.email || "—"}</p>
                        <p><span className="text-[#51637a]">Phone:</span> {extracted.phone || "—"}</p>
                        <p><span className="text-[#51637a]">Location:</span> {extracted.location || "—"}</p>
                        <p><span className="text-[#51637a]">Headline:</span> {extracted.headline || "—"}</p>
                        <p><span className="text-[#51637a]">Experience:</span> {extracted.years_experience || "—"}</p>
                      </div>
                      {extracted.summary && (
                        <p className="mt-3 text-sm text-[#122c47] leading-relaxed"><span className="text-[#51637a]">Summary:</span> {extracted.summary}</p>
                      )}
                      {extracted.skills?.length > 0 && (
                        <div className="mt-4">
                          <p className="text-xs uppercase tracking-[0.15em] font-medium text-[#51637a]">Skills</p>
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {extracted.skills.map((s) => (
                              <span key={s} className="rounded-full bg-white border border-[#e3e0d3] text-xs px-2.5 py-1 text-[#0d2f4d]">{s}</span>
                            ))}
                          </div>
                        </div>
                      )}
                      {extracted.work_history?.length > 0 && (
                        <div className="mt-4">
                          <p className="text-xs uppercase tracking-[0.15em] font-medium text-[#51637a]">Work history</p>
                          <ul className="mt-2 space-y-3">
                            {extracted.work_history.map((w, i) => (
                              <li key={i} className="bg-white border border-[#e3e0d3] rounded-md px-4 py-3">
                                <p className="text-sm font-medium text-[#0d2f4d]">{w.title} — {w.company} {w.duration && <span className="text-[#51637a] font-normal">({w.duration})</span>}</p>
                                {w.description && <p className="text-xs text-[#51637a] mt-1 leading-relaxed">{w.description}</p>}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {(extracted.qualifications?.length > 0 || extracted.certifications?.length > 0 || extracted.education?.length > 0) && (
                        <div className="mt-4">
                          <p className="text-xs uppercase tracking-[0.15em] font-medium text-[#51637a]">Qualifications & certifications</p>
                          <ul className="mt-2 space-y-1 text-sm text-[#122c47]">
                            {(extracted.qualifications || []).map((q) => <li key={q}>• {q}</li>)}
                            {(extracted.certifications || []).map((c) => <li key={c}>• {c}</li>)}
                            {(extracted.education || []).map((ed, i) => (
                              <li key={i}>• {[ed.qualification, ed.institution, ed.year].filter(Boolean).join(" — ")}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <button onClick={applyExtracted} data-testid="apply-extracted-button"
                        className="mt-5 rounded-full bg-[#0d2f4d] text-white text-sm font-medium px-6 py-2.5 hover:bg-[#1c4d78] transition-colors">
                        Apply to my profile
                      </button>
                    </div>
                  )}
                  {(!builderReq || builderReq.status === "draft") && (
                    <ResumeReviewCard testId="onboarding-review-upsell"
                      ctaLabel={payBusy ? "Redirecting..." : "Get my CV reviewed"}
                      onAction={() => startCheckout("review", {})} />
                  )}
                </>
              )}

              {cvMode === "build" && (!builderReq || builderReq.status === "draft") && (
                <div className="space-y-6" data-testid="cv-builder-wizard">
                  <p className="text-sm text-[#51637a] leading-relaxed max-w-2xl">
                    No CV? No problem. Tell us about your work below — rough notes are fine. Our team turns your answers
                    into a professional, market-ready CV within 48 hours, built for Australian recruiters and ATS systems.
                  </p>
                  <div>
                    <h3 className="font-heading font-bold text-[#0d2f4d]">Your roles</h3>
                    <p className="text-xs text-[#51637a] mt-1">Start with your most recent job. Add as many as you like.</p>
                    <div className="mt-4 space-y-4">
                      {builder.roles.map((r, i) => (
                        <div key={i} className="relative border border-[#e3e0d3] bg-white rounded-lg p-5 space-y-3" data-testid={`builder-role-${i}`}>
                          {builder.roles.length > 1 && (
                            <button onClick={() => setBuilder({ ...builder, roles: builder.roles.filter((_, x) => x !== i) })}
                              data-testid={`builder-role-remove-${i}`}
                              className="absolute top-4 right-4 text-[#51637a] hover:text-red-600 transition-colors"><Trash2 size={15} /></button>
                          )}
                          <div className="grid sm:grid-cols-2 gap-3">
                            <div><label className={labelCls}>Job title</label>
                              <input value={r.title} placeholder="e.g. Head Chef" onChange={(e) => setRole(i, "title", e.target.value)} data-testid={`builder-role-title-${i}`} className={inputCls} /></div>
                            <div><label className={labelCls}>Company</label>
                              <input value={r.company} placeholder="e.g. The Wharf Kitchen" onChange={(e) => setRole(i, "company", e.target.value)} data-testid={`builder-role-company-${i}`} className={inputCls} /></div>
                          </div>
                          <div><label className={labelCls}>When (roughly)</label>
                            <input value={r.duration} placeholder="e.g. 2019 – 2023" onChange={(e) => setRole(i, "duration", e.target.value)} data-testid={`builder-role-duration-${i}`} className={inputCls} /></div>
                          <div><label className={labelCls}>What did you do there? (dot points are fine)</label>
                            <textarea rows={3} value={r.description} placeholder="Ran a team of 8, managed orders and stock, handled service for 200+ covers a night..." onChange={(e) => setRole(i, "description", e.target.value)} data-testid={`builder-role-description-${i}`} className={inputCls} /></div>
                        </div>
                      ))}
                    </div>
                    <button onClick={() => setBuilder({ ...builder, roles: [...builder.roles, { title: "", company: "", duration: "", description: "" }] })}
                      data-testid="builder-add-role"
                      className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-[#7C8514] hover:gap-2.5 transition-all">
                      <Plus size={15} /> Add another role
                    </button>
                  </div>
                  <div><label className={labelCls}>Your skills (separate with commas)</label>
                    <input value={builder.skills} placeholder="e.g. kitchen management, menu development, food safety" onChange={(e) => setBuilder({ ...builder, skills: e.target.value })} data-testid="builder-skills" className={inputCls} /></div>
                  <div><label className={labelCls}>Qualifications, tickets & certificates (one per line)</label>
                    <textarea rows={3} value={builder.qualifications} placeholder={"Cert III Commercial Cookery\nFood Safety Supervisor"} onChange={(e) => setBuilder({ ...builder, qualifications: e.target.value })} data-testid="builder-qualifications" className={inputCls} /></div>
                  <div><label className={labelCls}>What kind of role are you after? Anything else we should know?</label>
                    <textarea rows={3} value={builder.goals} placeholder="Looking for head chef roles in Melbourne, keen on venues with growth opportunities..." onChange={(e) => setBuilder({ ...builder, goals: e.target.value })} data-testid="builder-goals" className={inputCls} /></div>
                  <ResumeReviewCard testId="onboarding-builder-pay"
                    ctaLabel={payBusy ? "Redirecting..." : "Pay A$207 & submit"}
                    onAction={() => startCheckout("build", builder)} />
                  <p className="text-xs text-[#51637a]">Your answers are saved when you submit. Payment is a one-off A$207 via Stripe — your curated CV lands within 48 hours.</p>
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div data-testid="step-uploads">
              <h2 className="font-heading font-bold text-lg text-[#0d2f4d] mb-5">Document uploads</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <UploadTile docType="photo" label="Profile photo" hint="JPG or PNG" accept="image/*" existingDocs={docs} onChange={load} />
                <UploadTile docType="credentials" label="Credentials" hint="Certificates, licences, tickets" existingDocs={docs} onChange={load} />
                <UploadTile docType="references" label="References" hint="Written references or referee contacts" existingDocs={docs} onChange={load} />
                <UploadTile docType="id" label="Photo ID" hint="Passport or driver licence" existingDocs={docs} onChange={load} />
                <UploadTile docType="police_check" label="Police check" hint="If you have one already" existingDocs={docs} onChange={load} />
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-6 max-w-xl" data-testid="step-consent">
              <h2 className="font-heading font-bold text-lg text-[#0d2f4d]">Consent & submit</h2>
              {consents.length > 0 ? (
                <p className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3" data-testid="consent-signed-banner">
                  <CheckCircle2 size={16} /> Consent signed by {consents[0].typed_signature} on {new Date(consents[0].signed_at).toLocaleDateString()}
                </p>
              ) : (
                <>
                  <div className="bg-[#fcfbf8] border border-[#e3e0d3] rounded-lg p-5 text-sm text-[#51637a] leading-relaxed max-h-44 overflow-y-auto">
                    <p className="font-medium text-[#0d2f4d] mb-2">Candidate Consent & Privacy Agreement</p>
                    <p>I consent to The Ditch Careers collecting, storing and processing my personal information, CV, credentials, references, identification and police check documents for the purpose of matching me with employment opportunities. I understand my details will only be shared with employers when I am shortlisted by a recruiter, and I may withdraw consent at any time by contacting The Ditch Careers.</p>
                  </div>
                  <div><label className={labelCls}>Type your full name as your signature</label>
                    <input value={signature} onChange={(e) => setSignature(e.target.value)} data-testid="consent-signature-input" className={inputCls} placeholder="Full legal name" /></div>
                  <label className="flex items-start gap-3 text-sm text-[#51637a] cursor-pointer">
                    <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="consent-agree-checkbox" className="mt-0.5 h-4 w-4 accent-[#C1CC24]" />
                    I have read and agree to the Candidate Consent & Privacy Agreement.
                  </label>
                  <button onClick={signConsent} disabled={!signature.trim() || !agreed} data-testid="sign-consent-button"
                    className="rounded-full bg-[#0d2f4d] text-white text-sm font-medium px-6 py-3 hover:bg-[#1c4d78] transition-colors disabled:opacity-50">
                    Sign consent
                  </button>
                </>
              )}
              <div className="border-t border-[#e3e0d3] pt-6">
                <h3 className="font-heading font-bold text-[#0d2f4d]">Submit for recruiter review</h3>
                <p className="text-sm text-[#51637a] mt-1">Once submitted, one of our recruiters will review your profile.</p>
                <button onClick={submitProfile} disabled={profile.submitted || consents.length === 0} data-testid="submit-profile-button"
                  className="mt-4 rounded-full bg-[#C1CC24] text-[#0d2f4d] text-sm font-medium px-7 py-3 hover:bg-[#aeb81f] transition-colors disabled:opacity-50">
                  {profile.submitted ? "Profile submitted ✓" : "Submit my profile"}
                </button>
                {consents.length === 0 && !profile.submitted && <p className="text-xs text-[#51637a] mt-2">Sign the consent above to enable submission.</p>}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-between mt-6">
          <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} data-testid="onboarding-back"
            className="inline-flex items-center gap-1 rounded-full border border-[#e3e0d3] bg-white text-[#0d2f4d] text-sm px-5 py-2.5 hover:border-[#0d2f4d] transition-colors disabled:opacity-40">
            <ChevronLeft size={16} /> Back
          </button>
          <button onClick={() => setStep(Math.min(STEPS.length - 1, step + 1))} disabled={step === STEPS.length - 1} data-testid="onboarding-next"
            className="inline-flex items-center gap-1 rounded-full bg-[#0d2f4d] text-white text-sm px-5 py-2.5 hover:bg-[#1c4d78] transition-colors disabled:opacity-40">
            Next <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </PortalLayout>
  );
}
